import { Injectable, NgZone, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Action, createSelector, Selector, State, StateContext } from '@ngxs/store';
import { produce } from 'immer';
import { Observable, of } from 'rxjs';
import { catchError, filter, finalize, map, switchMap, tap } from 'rxjs/operators';
import { RuleSet, SeasonPlan } from '@coin/shared/core/util-models';
import { TinyHelpers } from '@coin/shared/core/util-helpers';
import { SeasonTypeEnum } from '@coin/shared/core/util-enums';
import { LoadingService } from '@coin/shared/core/data-access';
import { GenericSeasonSettings, Season } from '@coin/shared/season-mgmt/util';
import { RuleEngineV2Service } from '@coin/shared/core/data-access';
import { SeasonPlanService, SeasonSettingsService } from '@coin/shared/season-mgmt/data-access';
import { SeasonSimulation } from '../models/season-simulation.model';
import { SeasonSimulationService } from '../services/season-simulation.service';
import { SeasonProcessStepNav } from '../interfaces/season-process-step-nav.interface';
import { GeneralSeasonService } from '../services/general-season.service';
import { SEASON_STEPS } from '../static/season-process-steps';
import {
  DeletePlan,
  LoadRuleSet,
  LoadSimulations,
  ResetCurrentPlan,
  ResetCurrentSeason,
  SetCurrentPlan,
  SetCurrentProcessStep,
  SetCurrentSeason,
  SetCurrentSeasonSettings,
  SetCurrentSimulation,
  SetPlan,
  SetPlans,
  SetPlanVersion,
  SetRuleSet,
  SetSeasonState,
  UpdateCurrentSeason,
  UpdateCurrentSeasonSettings,
  UpdateExceptionalRecognitionSeasonState,
  UpdateSpecialPaymentSeasonState,
  UpdatePlanTitle,
  UpdateProcessStep,
  UpdateSeasonPlan,
  UpdateSimulation
} from './season.actions';
import { SpecialPaymentSeasonService } from '../services/special-payment-season.service';
import { ExceptionalRecognitionSeasonService } from '../services/exceptional-recognition-season.service';

export interface SeasonStateModel {
  currentSeason: Season;
  currentSimulation: SeasonSimulation;
  currentSeasonSettings: GenericSeasonSettings;
  currentPlan: SeasonPlan;
  breadcrumbItems: SeasonProcessStepNav[];
  ruleSet: RuleSet;
  simulations: SeasonSimulation[];
  plans: SeasonPlan[];
  ruleSetPopulation: {
    ruleSet: RuleSet;
    population: unknown;
    skip: number;
  };
}

@State<SeasonStateModel>({
  name: 'seasonState',
  defaults: {
    currentSeason: null,
    currentSeasonSettings: null,
    currentSimulation: null,
    currentPlan: null,
    breadcrumbItems: [],
    ruleSet: null,
    ruleSetPopulation: {
      ruleSet: null,
      population: null,
      skip: 0
    },
    simulations: [],
    plans: []
  }
})
@Injectable()
export class SeasonState {
  private generalSeasonService = inject(GeneralSeasonService);
  private specialPaymentSeasonService = inject(SpecialPaymentSeasonService);
  private exceptionalRecognitionSeasonService = inject(ExceptionalRecognitionSeasonService);
  private seasonSettingsService = inject(SeasonSettingsService);
  private router = inject(Router);
  private ngZone = inject(NgZone);
  private seasonPlanService = inject(SeasonPlanService);
  private ruleEngineV2Service = inject(RuleEngineV2Service);
  private seasonSimulationService = inject(SeasonSimulationService);
  private loadingService = inject(LoadingService);

  @Selector()
  static breadcrumbItems(state: SeasonStateModel): SeasonProcessStepNav[] {
    return state.breadcrumbItems;
  }

  @Selector()
  static currentSeason(state: SeasonStateModel): Season {
    return state.currentSeason;
  }

  @Selector()
  static currentSeasonSettings(state: SeasonStateModel): GenericSeasonSettings {
    return state.currentSeasonSettings;
  }

  @Selector()
  static currentSimulation(state: SeasonStateModel): SeasonSimulation {
    return state.currentSimulation;
  }

  @Selector()
  static currentPlan(state: SeasonStateModel): SeasonPlan {
    return state.currentPlan;
  }

  @Selector()
  static ruleSet(state: SeasonStateModel): RuleSet {
    return state.ruleSet;
  }

  @Selector()
  static population(state: SeasonStateModel): unknown {
    return state.ruleSetPopulation.population;
  }

  @Selector()
  static simulations(state: SeasonStateModel): SeasonSimulation[] {
    return state.simulations;
  }

  @Selector()
  static plans(state: SeasonStateModel): SeasonPlan[] {
    return state.plans;
  }

  @Selector()
  static currentSeasonAvailable(state: SeasonStateModel): boolean {
    return !!state.currentSeason.id;
  }

  @Selector()
  static currentProcessStep(state: SeasonStateModel): SeasonProcessStepNav {
    return state.breadcrumbItems.find(item => item.active);
  }

  static getProcessStepById(id: string): (s: SeasonStateModel) => SeasonProcessStepNav {
    return createSelector([SeasonState], (state: SeasonStateModel) => {
      return state.breadcrumbItems.find(item => item.id === id);
    });
  }

  static getProcessStepByRoute(route: string): (s: SeasonStateModel) => SeasonProcessStepNav {
    return createSelector([SeasonState], (state: SeasonStateModel) => {
      return state.breadcrumbItems.find(item => item.route === route);
    });
  }

  @Selector()
  static nextProcessStep(state: SeasonStateModel): SeasonProcessStepNav {
    for (let i = 0; i < state.breadcrumbItems.length; i++) {
      if (state.breadcrumbItems[i].active) {
        return state.breadcrumbItems[i + 1];
      }
    }
    return undefined;
  }

  @Selector()
  static prevProcessStep(state: SeasonStateModel): SeasonProcessStepNav {
    for (let i = 0; i < state.breadcrumbItems.length; i++) {
      if (state.breadcrumbItems[i].active) {
        return state.breadcrumbItems[i - 1];
      }
    }
    return undefined;
  }

  @Action(SetCurrentProcessStep)
  setCurrentProcessStep(ctx: StateContext<SeasonStateModel>, { payload }: SetCurrentProcessStep): void {
    ctx.setState(
      produce(ctx.getState(), state => {
        for (const item of state.breadcrumbItems) {
          item.active = item.id === payload;
        }
      })
    );
  }

  @Action(UpdateProcessStep)
  updateProcessStep(ctx: StateContext<SeasonStateModel>, { payload }: UpdateProcessStep): void {
    ctx.setState(
      produce(ctx.getState(), state => {
        const index = state.breadcrumbItems.findIndex(item => item.id === payload.id);
        if (index > -1) {
          state.breadcrumbItems[index] = { ...state.breadcrumbItems[index], ...payload };
        }
      })
    );
  }

  @Action(SetRuleSet)
  SetRuleSet(ctx: StateContext<SeasonStateModel>, { payload }: SetRuleSet): void {
    ctx.setState(
      produce(ctx.getState(), state => {
        if (payload && !TinyHelpers.areItemsSimilar(payload, state.ruleSet)) {
          state.ruleSet = structuredClone(payload);
        }
      })
    );
  }

  @Action(LoadRuleSet)
  loadRuleSet(ctx: StateContext<SeasonStateModel>, { payload }: LoadRuleSet): Observable<void> {
    return this.ruleEngineV2Service
      .getRuleSet(payload.ruleSetId, false)
      .pipe(switchMap(res => ctx.dispatch(new SetRuleSet(res as unknown as RuleSet))));
  }

  @Action(LoadSimulations)
  loadSimulations(ctx: StateContext<SeasonStateModel>, { payload }: LoadSimulations): Observable<void> {
    return this.seasonSimulationService.getSimulations(payload.seasonType, payload.seasonId).pipe(
      map((res: SeasonSimulation[]) => {
        ctx.setState(
          produce(ctx.getState(), state => {
            if (!TinyHelpers.areItemsSimilar(res, state.simulations)) {
              state.simulations = res;
            }
          })
        );
      })
    );
  }

  @Action(UpdateSimulation)
  updateSimulation(
    ctx: StateContext<SeasonStateModel>,
    { payload }: UpdateSimulation
  ): Observable<void | SeasonSimulation> {
    return this.seasonSimulationService.updateSimulation(payload.seasonType, payload.simulation).pipe(
      tap(() => {
        ctx.setState(
          produce(ctx.getState(), state => {
            state.simulations = state.simulations?.map(sim =>
              sim.id === payload?.simulation?.id ? { ...sim, ...payload } : sim
            );
          })
        );
      })
    );
  }

  @Action(SetPlans)
  setPlans(ctx: StateContext<SeasonStateModel>, { payload }: SetPlans): void {
    ctx.setState(
      produce(ctx.getState(), state => {
        if (!TinyHelpers.areItemsSimilar(payload, state.plans)) {
          state.plans = payload;
        }
      })
    );
  }

  @Action(UpdateSeasonPlan)
  updateSeasonPlan(ctx: StateContext<SeasonStateModel>, { payload }: UpdateSeasonPlan): void {
    ctx.setState(
      produce(ctx.getState(), state => {
        const foundIndex = state.plans.findIndex(plan => plan.id === payload.id);
        state.plans[foundIndex] = { ...state.plans[foundIndex], ...payload };
        if (state.currentPlan?.id === state.plans[foundIndex]?.id) {
          state.currentPlan = state.plans[foundIndex];
        }
      })
    );
  }

  @Action(UpdatePlanTitle)
  updatePlanTitle(ctx: StateContext<SeasonStateModel>, { payload }: UpdatePlanTitle): Observable<void> {
    return this.seasonPlanService
      .updatePlan(payload.seasonType, { ...payload.plan, title: payload.newTitle, displayName: payload.displayName })
      .pipe(
        map((res: SeasonPlan) => {
          ctx.setState(
            produce(ctx.getState(), state => {
              const changedPlan = state.plans.find(plan => plan.id === payload.plan.id);
              changedPlan.title = payload.newTitle;
              changedPlan.displayName = payload.displayName;
              changedPlan.version = res.version;
            })
          );
        })
      );
  }

  @Action(DeletePlan)
  deletePlan(ctx: StateContext<SeasonStateModel>, { payload }: DeletePlan): Observable<void> {
    return this.seasonPlanService
      .deletePlan(payload.seasonType, payload.plan.id)
      .pipe(
        switchMap(() =>
          ctx.dispatch(new SetCurrentSeason(ctx.getState().currentSeason?.id, ctx.getState().currentSeason?.seasonType))
        )
      );
  }

  @Action(SetCurrentSeason)
  setCurrentSeason(
    ctx: StateContext<SeasonStateModel>,
    { seasonId, seasonType }: SetCurrentSeason
  ): Observable<unknown> {
    return this.generalSeasonService.getSeasonById(seasonId, seasonType).pipe(
      tap((season: Season) => {
        ctx.setState(
          produce(ctx.getState(), state => {
            if (season) {
              state.plans = season?.plans || [];
              state.currentSeason = season;

              if (!state.breadcrumbItems?.length) {
                state.breadcrumbItems = structuredClone(SEASON_STEPS[season.seasonType]);
              }
            }
          })
        );
      }),
      switchMap(() => ctx.dispatch(new SetCurrentSeasonSettings())),
      catchError(err => {
        this.ngZone.run(() => this.router.navigate(['/season-mgmt/season-overview']));
        return err;
      })
    );
  }

  @Action(UpdateCurrentSeason)
  updateCurrentSeason(ctx: StateContext<SeasonStateModel>, { payload }: UpdateCurrentSeason): Observable<Season> {
    return this.generalSeasonService.updateSeason(payload).pipe(
      tap(season => {
        ctx.setState(
          produce(ctx.getState(), state => {
            state.currentSeason = season;
          })
        );
      })
    );
  }

  @Action(SetCurrentSeasonSettings)
  setCurrentSeasonSettings(ctx: StateContext<SeasonStateModel>): Observable<GenericSeasonSettings> {
    const season = ctx.getState().currentSeason;

    if (
      ![
        SeasonTypeEnum.Merit,
        SeasonTypeEnum.SpecialPaymentCash,
        SeasonTypeEnum.SpecialPaymentEquity,
        SeasonTypeEnum.PositionEvaluation,
        SeasonTypeEnum.IncentiveV2,
        SeasonTypeEnum.ExceptionalRecognitions
      ].includes(season?.seasonType)
    ) {
      return of(null);
    }

    return this.seasonSettingsService.getSeasonSettings(season?.id, season?.seasonType).pipe(
      filter(settings => !!settings),
      catchError(() => of(null)),
      tap(currentSeasonSettings => ctx.patchState({ currentSeasonSettings }))
    );
  }

  @Action(UpdateCurrentSeasonSettings)
  updateCurrentSeasonSettings(
    ctx: StateContext<SeasonStateModel>,
    { settings, seasonType }: UpdateCurrentSeasonSettings
  ): Observable<GenericSeasonSettings> {
    return this.seasonSettingsService.updateSeasonSettings(settings, seasonType).pipe(
      filter(settings => !!settings),
      tap(currentSeasonSettings => ctx.patchState({ currentSeasonSettings }))
    );
  }

  @Action(UpdateSpecialPaymentSeasonState)
  updateSpecialPaymentSeasonState(
    ctx: StateContext<SeasonStateModel>,
    { seasonId, newState }: UpdateSpecialPaymentSeasonState
  ): Observable<Season> {
    return this.specialPaymentSeasonService.updateSeasonState(seasonId, newState).pipe(
      tap(season => {
        ctx.setState(
          produce(ctx.getState(), state => {
            state.currentSeason = season;
          })
        );
      })
    );
  }

  @Action(UpdateExceptionalRecognitionSeasonState)
  updateExceptionalRecognitionSeasonState(
    ctx: StateContext<SeasonStateModel>,
    { seasonId, newState }: UpdateExceptionalRecognitionSeasonState
  ): Observable<Season> {
    return this.exceptionalRecognitionSeasonService.updateSeasonState(seasonId, newState).pipe(
      tap(season => {
        ctx.setState(
          produce(ctx.getState(), state => {
            state.currentSeason = season;
          })
        );
      })
    );
  }

  @Action(SetCurrentPlan)
  setCurrentPlan(ctx: StateContext<SeasonStateModel>, { payload }: SetCurrentPlan): Observable<unknown> {
    return this.seasonPlanService.getPlanById(payload.seasonType, payload.planId).pipe(
      tap((plan: SeasonPlan) => {
        ctx.setState(
          produce(ctx.getState(), state => {
            if (plan) {
              state.currentPlan = plan;

              if (!state.plans.some(storedPlan => storedPlan.id === plan.id)) {
                state.plans.push(plan);
              }
            }
          })
        );
      }),
      catchError(err => {
        this.ngZone.run(() => this.router.navigate(['/season-mgmt/season-overview']));
        return err;
      })
    );
  }

  @Action(SetCurrentSimulation)
  setCurrentSimulation(ctx: StateContext<SeasonStateModel>, { payload }: SetCurrentSimulation): Observable<unknown> {
    this.loadingService.present();
    return this.seasonSimulationService.getSimulationById(payload.seasonType, payload.simulationId).pipe(
      tap((sim: SeasonSimulation) => {
        ctx.setState(
          produce(ctx.getState(), state => {
            if (sim) {
              state.currentSimulation = sim;

              if (!state.simulations.some(storedSim => storedSim.id === sim.id)) {
                state.simulations.push(sim);
              }
            }
          })
        );
      }),
      catchError(err => {
        this.ngZone.run(() => this.router.navigate(['/season-mgmt/season-overview']));
        return err;
      }),
      finalize(() => this.loadingService.dismiss())
    );
  }

  @Action(SetSeasonState)
  setSeasonState(ctx: StateContext<SeasonStateModel>, { payload }: SetSeasonState): void {
    ctx.setState(
      produce(ctx.getState(), state => {
        if (payload) {
          state.currentSeason.state = payload;
        }
      })
    );
  }

  @Action(SetPlanVersion)
  setPlanVersion(ctx: StateContext<SeasonStateModel>, { payload }: SetPlanVersion): void {
    ctx.setState(
      produce(ctx.getState(), state => {
        if (payload) {
          state.currentPlan.version = payload;
        }
      })
    );
  }

  @Action(SetPlan)
  setPlan(ctx: StateContext<SeasonStateModel>, { payload }: SetPlan): void {
    ctx.setState(
      produce(ctx.getState(), state => {
        if (payload) {
          state.currentPlan = payload;
        }
      })
    );
  }

  @Action(ResetCurrentSeason)
  resetCurrentSeason(ctx: StateContext<SeasonStateModel>): void {
    ctx.setState(
      produce(ctx.getState(), state => {
        state.currentSeason = null;
        state.currentSeasonSettings = null;
        state.currentPlan = null;
        state.currentSimulation = null;
        state.breadcrumbItems = [];
        state.ruleSet = null;
        state.ruleSetPopulation = {
          population: null,
          ruleSet: null,
          skip: 0
        };
        state.simulations = [];
        state.plans = [];
      })
    );
  }

  @Action(ResetCurrentPlan)
  resetCurrentPlan(ctx: StateContext<SeasonStateModel>): void {
    ctx.setState(
      produce(ctx.getState(), state => {
        state.currentPlan = null;
      })
    );
  }
}
